import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ReferenceValueSynonym } from "@/entities/all";

export default function CreateSynonymModal({ isOpen, onClose, onSynonymCreated, values, selectedValueId = null }) {
  const [formData, setFormData] = useState({
    reference_value_id: selectedValueId || '',
    synonym: '',
    synonym_type: 'Alternative Name',
    confidence_score: 0.9,
    source: 'User Input',
    is_approved: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (selectedValueId) {
      setFormData(prev => ({ ...prev, reference_value_id: selectedValueId }));
    }
  }, [selectedValueId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await ReferenceValueSynonym.create(formData);
      onSynonymCreated();
      setFormData({
        reference_value_id: selectedValueId || '',
        synonym: '',
        synonym_type: 'Alternative Name',
        confidence_score: 0.9,
        source: 'User Input',
        is_approved: true
      });
    } catch (error) {
      console.error('Error creating synonym:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Synonym</DialogTitle>
          <DialogDescription>
            Create a new synonym for a canonical reference value.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label htmlFor="reference_value_id">Canonical Value</Label>
            <Select value={formData.reference_value_id} onValueChange={(value) => handleChange('reference_value_id', Number(value))}>
              <SelectTrigger>
                <SelectValue placeholder="Select canonical value" />
              </SelectTrigger>
              <SelectContent>
                {values.map(value => (
                  <SelectItem key={value.id} value={value.id}>
                    {value.canonical_value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="synonym">Synonym Value</Label>
            <Input
              id="synonym"
              value={formData.synonym}
              onChange={(e) => handleChange('synonym', e.target.value)}

              required
            />
          </div>

          <div>
            <Label htmlFor="synonym_type">Synonym Type</Label>
            <Select value={formData.synonym_type} onValueChange={(value) => handleChange('synonym_type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Abbreviation">Abbreviation</SelectItem>
                <SelectItem value="Alternative Name">Alternative Name</SelectItem>
                <SelectItem value="Legacy Value">Legacy Value</SelectItem>
                <SelectItem value="Common Misspelling">Common Misspelling</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="confidence_score">Confidence Score</Label>
              <Input
                id="confidence_score"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={formData.confidence_score}
                onChange={(e) => handleChange('confidence_score', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="source">Source</Label>
              <Input
                id="source"
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
                placeholder="e.g., User Input, Data Analysis"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Synonym'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
